import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe } from 'lucide-react';

interface LanguageSuggestionBannerProps {
  detectedCountry?: string;
  suggestedLanguage?: string;
  onLanguageSwitch?: (language: string) => void;
  onDismiss?: () => void;
  currentPage?: string;
}

export const LanguageSuggestionBanner: React.FC<LanguageSuggestionBannerProps> = ({
  detectedCountry,
  suggestedLanguage,
  onLanguageSwitch,
  onDismiss,
  currentPage
}) => {
  const { t, i18n } = useTranslation('seo');
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Mapping pays -> langue suggérée
  const countryToLanguage: Record<string, string> = {
    'FR': 'fr',
    'ES': 'es',
    'DE': 'de',
    'IT': 'it',
    'PT': 'pt',
    'NL': 'nl',
    'PL': 'pl',
    'RU': 'ru',
    'CN': 'zh',
    'JP': 'ja',
    'KR': 'ko',
    'AR': 'ar',
    'SA': 'ar',
    'AE': 'ar-AE',
    'EG': 'ar-EG',
    'MA': 'ar-MA',
    'CH': 'de-CH', // Suisse allemande par défaut
    'MX': 'es-MX',
    'BR': 'pt-BR',
    'IN': 'hi',
    'TH': 'th',
    'VN': 'vi',
    'ID': 'id',
    'MY': 'ms',
    'TR': 'tr',
    'GR': 'el',
    'BG': 'bg',
    'RO': 'ro',
    'HU': 'hu',
    'CZ': 'cs',
    'SK': 'sk',
    'SI': 'sl',
    'HR': 'hr',
    'LT': 'lt',
    'LV': 'lv',
    'EE': 'et',
    'FI': 'fi',
    'SE': 'sv',
    'NO': 'no',
    'DK': 'da',
    'IL': 'he',
    'IR': 'fa',
    'UA': 'uk'
  };

  // Déterminer la langue suggérée selon les bonnes pratiques Google SEO
  const getLanguageSuggestion = (): string | null => {
    if (suggestedLanguage) return suggestedLanguage;
    
    // 1. Priorité : Langue du navigateur (recommandé Google)
    const browserLanguages = navigator.languages || [navigator.language];
    
    // Langues supportées par Komby.io (52 langues exactement)
    const supportedLanguages = [
      'en', 'en-US', 'en-CA', 'en-AU', 'en-GB', 'en-SG', 'en-ZA', 'en-NZ',
      'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'zh', 'ja', 'ar', 'ko',
      'cs', 'pt-BR', 'sv', 'fr-CH', 'es-MX', 'no', 'da', 'fi', 'sk', 'sl',
      'hr', 'bg', 'ro', 'th', 'vi', 'id', 'ms', 'hi', 'el', 'hu', 'lv', 'lt',
      'et', 'uk', 'tr', 'fa', 'he', 'ar-EG', 'ar-AE', 'ar-MA', 'de-CH', 'it-CH', 'es-ES'
    ];
    
    for (const browserLang of browserLanguages) {
      // Tester d'abord la langue complète (ex: fr-CA)
      if (supportedLanguages.includes(browserLang)) {
        return browserLang;
      }
      // Puis tester juste le code langue (ex: fr)
      const shortLang = browserLang.split('-')[0];
      if (supportedLanguages.includes(shortLang)) {
        return shortLang;
      }
    }
    
    // 2. Fallback : Géolocalisation pays
    if (detectedCountry) {
      const countryLang = countryToLanguage[detectedCountry];
      if (countryLang) {
        return countryLang;
      }
    }
    
    return null;
  };

  // Vérifier si la bannière doit être affichée
  useEffect(() => {
    const checkShouldShow = () => {
      // Ne pas afficher si déjà dismissée (avec expiration 30 jours)
      const dismissedData = localStorage.getItem('language-banner-dismissed');
      if (dismissedData) {
        try {
          const { dismissed, timestamp } = JSON.parse(dismissedData);
          const EXPIRY_DAYS = 30;
          const isExpired = Date.now() - timestamp > EXPIRY_DAYS * 24 * 60 * 60 * 1000;
          
          if (dismissed && !isExpired) {
            return false;
          } else if (isExpired) {
            localStorage.removeItem('language-banner-dismissed');
          }
        } catch (e) {
          // Format invalide, supprimer
          localStorage.removeItem('language-banner-dismissed');
        }
      }

      // Ne pas afficher si pas de suggestion
      const suggestion = getLanguageSuggestion();
      if (!suggestion) {
        return false;
      }

      // Ne pas afficher si déjà dans la langue suggérée
      if (i18n.language === suggestion) {
        return false;
      }

      // Ne pas afficher si langue suggérée est une variante de la langue actuelle
      const currentLangBase = i18n.language.split('-')[0];
      const suggestedLangBase = suggestion.split('-')[0];
      if (currentLangBase === suggestedLangBase) {
        return false;
      }

      return true;
    };

    if (checkShouldShow()) {
      // Délai pour éviter le flash
      setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, 1000);
    }
  }, [detectedCountry, suggestedLanguage, i18n.language]);

  const handleSwitch = () => {
    const suggestion = getLanguageSuggestion();
    if (suggestion) {
      setIsAnimating(false);
      setTimeout(() => {
        setIsVisible(false);
        onLanguageSwitch?.(suggestion);
      }, 300);
    }
  };

  const handleDismiss = () => {
    const dismissData = {
      dismissed: true,
      timestamp: Date.now()
    };
    localStorage.setItem('language-banner-dismissed', JSON.stringify(dismissData));
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onDismiss?.();
    }, 300);
  };

  // Obtenir les traductions pour la bannière (dans la langue actuelle)
  const getBannerTranslations = () => {
    const suggestion = getLanguageSuggestion();
    if (!suggestion) return null;

    // Obtenir le nom de la langue suggérée dans la langue actuelle
    const getLanguageName = (langCode: string) => {
      const languageNames: Record<string, Record<string, string>> = {
        'fr': {
          'en': 'anglais', 'fr': 'français', 'es': 'espagnol', 'de': 'allemand', 'it': 'italien',
          'pt': 'portugais', 'nl': 'néerlandais', 'pl': 'polonais', 'ru': 'russe', 'zh': 'chinois',
          'ja': 'japonais', 'ko': 'coréen', 'ar': 'arabe', 'th': 'thaï', 'vi': 'vietnamien',
          'hi': 'hindi', 'tr': 'turc', 'he': 'hébreu', 'fa': 'persan', 'pt-BR': 'portugais (Brésil)',
          'id': 'indonésien', 'ms': 'malais', 'el': 'grec', 'hu': 'hongrois', 'cs': 'tchèque'
        },
        'en': {
          'en': 'English', 'fr': 'French', 'es': 'Spanish', 'de': 'German', 'it': 'Italian',
          'pt': 'Portuguese', 'nl': 'Dutch', 'pl': 'Polish', 'ru': 'Russian', 'zh': 'Chinese',
          'ja': 'Japanese', 'ko': 'Korean', 'ar': 'Arabic', 'th': 'Thai', 'vi': 'Vietnamese',
          'hi': 'Hindi', 'tr': 'Turkish', 'he': 'Hebrew', 'fa': 'Persian', 'pt-BR': 'Portuguese (Brazil)',
          'id': 'Indonesian', 'ms': 'Malay', 'el': 'Greek', 'hu': 'Hungarian', 'cs': 'Czech'
        }
      };
      
      const currentLang = i18n.language.split('-')[0];
      return languageNames[currentLang]?.[langCode.split('-')[0]] || langCode;
    };

    const suggestedLanguageName = getLanguageName(suggestion);
    const currentLang = i18n.language.split('-')[0];

    // Messages génériques dans la langue actuelle
    const messages = {
      'fr': {
        title: `Préférez-vous le ${suggestedLanguageName} ?`,
        message: `Il semble que vous parliez ${suggestedLanguageName} selon votre navigateur. Souhaitez-vous changer ?`,
        switchButton: `Passer en ${suggestedLanguageName}`,
        dismissButton: 'Rester en français'
      },
      'en': {
        title: `Would you prefer ${suggestedLanguageName}?`,
        message: `It looks like you speak ${suggestedLanguageName} based on your browser. Would you like to switch?`,
        switchButton: `Switch to ${suggestedLanguageName}`,
        dismissButton: 'Stay in English'
      }
    };

    return messages[currentLang] || {
      title: `Switch to ${suggestedLanguageName}?`,
      message: `It looks like you prefer ${suggestedLanguageName}. Would you like to switch?`,
      switchButton: `Switch to ${suggestedLanguageName}`,
      dismissButton: 'Dismiss'
    };
  };

  if (!isVisible) return null;

  const translations = getBannerTranslations();
  if (!translations) return null;

  const suggestion = getLanguageSuggestion();
  const isRTL = suggestion?.startsWith('ar') || suggestion === 'he' || suggestion === 'fa';
  
  const isHomepageNoNav = currentPage === 'coming-soon' || currentPage === 'home';
  const topPosition = isHomepageNoNav ? '0px' : '64px';

  return (
    <div
      className={`fixed left-0 right-0 z-[70] transform transition-all duration-300 ease-in-out ${
        isAnimating ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      }`}
      style={{ top: topPosition }}
    >
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-2 sm:py-3">
          <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
            <div className={`flex items-center space-x-2 sm:space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
              <Globe className="h-5 w-5 flex-shrink-0" />
              <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
                <h3 className="font-semibold text-sm leading-tight">
                  {translations.title}
                </h3>
                <p className="text-xs opacity-90 mt-0.5 sm:mt-1 leading-tight">
                  {translations.message}
                </p>
              </div>
            </div>

            <div className={`flex items-center space-x-2 flex-shrink-0 ${isRTL ? 'space-x-reverse' : ''}`}>
              <button
                onClick={handleSwitch}
                className="bg-yellow-400 text-gray-900 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-bold hover:bg-yellow-500 transition-colors duration-200 whitespace-nowrap shadow-lg"
              >
                {translations.switchButton}
              </button>
              <button
                onClick={handleDismiss}
                className="text-white hover:text-yellow-300 transition-colors duration-200 ml-2"
                aria-label={translations.dismissButton}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSuggestionBanner;
